import React, { ComponentProps, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { GridColDef } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { Avatar, LightChip } from '@atoms/dataDisplay';
import { Box, Stack, Tooltip } from '@mui/material';
import { AgencyListItem } from '@libs/models/agencies';
import { FacilityListItem } from '@libs/models/facilities';
import { OrganizationListItem } from '@libs/models/organization';
import dayjs from 'dayjs';
import { getActionMenuColumn } from '@molecules/dataDisplay/dataGrid';
import { useAgenciesListStore } from '@libs/store/agencies/agenciesListStore';
import { useFacilitiesListStore } from '@libs/store/facilities/facilitiesListStore';
import { InvitationRequestListItem } from '@libs/models/invitationRequests';
import { Button } from '@atoms/buttons';
import { useClientRequestsListStore } from '@libs/store/clientRequests';

export function useRequestsListColumns(localizationSubset: string) {
  const intl = useIntl();
  const { activateEntity, deactivateEntity } = useClientRequestsListStore();
  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<InvitationRequestListItem>[] = [
      getOrgTypeColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getCreatedColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getNameColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getTenantIdColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getAddressColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getPhoneColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getMainContactColumn(intl, localizationSubset) as GridColDef<InvitationRequestListItem>,
      getQuickActionsColumn(
        intl,
        localizationSubset,
        (entity) => {
          activateEntity(entity);
        },
        (entity) => {
          deactivateEntity(entity);
        },
      ) as GridColDef<InvitationRequestListItem>,
    ];
    return columns;
  }, [intl]);
}

export function useAgenciesListColumns(localizationSubset: string) {
  const intl = useIntl();
  const { activateFacility, deactivateFacility } = useFacilitiesListStore();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<AgencyListItem>[] = [
      getCreatedColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getNameColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getAgencyTypeColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getLinesOfBusinessColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getTenantIdColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getAddressColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getMainContactColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getPhoneColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getInviteStatusColumn(intl, localizationSubset) as GridColDef<AgencyListItem>,
      getActionMenuColumn([
        {
          label: intl.formatMessage({
            id: `${localizationSubset}.table.fields.actionMenu.activate.label`,
          }),
          key: 'activate',
          onClick: (data) => {
            activateFacility(data.id);
          },
        },
        {
          label: intl.formatMessage({
            id: `${localizationSubset}.table.fields.actionMenu.deactivate.label`,
          }),
          key: 'deactivate',
          onClick: (data) => {
            deactivateFacility(data.id);
          },
        },
      ]) as GridColDef<AgencyListItem>,
    ];
    return columns;
  }, [intl]);
}

export function useFacilitiesListColumns(localizationSubset: string) {
  const intl = useIntl();
  const { activateAgency, deactivateAgency } = useAgenciesListStore();
  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<FacilityListItem>[] = [
      getCreatedColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getNameColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getTenantIdColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getLinesOfBusinessColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getAddressColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getMainContactColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getPhoneColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getInviteStatusColumn(intl, localizationSubset) as GridColDef<FacilityListItem>,
      getActionMenuColumn([
        {
          label: intl.formatMessage({
            id: `${localizationSubset}.table.fields.actionMenu.activate.label`,
          }),
          key: 'activate',
          onClick: (data) => {
            activateAgency(data.id);
          },
        },
        {
          label: intl.formatMessage({
            id: `${localizationSubset}.table.fields.actionMenu.deactivate.label`,
          }),
          key: 'deactivate',
          onClick: (data) => {
            deactivateAgency(data.id);
          },
        },
      ]) as GridColDef<FacilityListItem>,
    ];
    return columns;
  }, [intl]);
}
/// ======================= Requests columns =======================
function getOrgTypeColumn(intl: IntlShape, localizationSubset: string): GridColDef<InvitationRequestListItem> {
  return {
    field: 'orgType',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.orgType.heading`,
    }),
    width: 10,
    sortable: true,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      return (
        <Chip
          label={intl.formatMessage({
            id: `${localizationSubset}.orgTypes.${params.value.toLowerCase()}.label`,
          })}
        />
      );
    },
  };
}
function getQuickActionsColumn(
  intl: IntlShape,
  localizationSubset: string,
  onActivate: (entity: InvitationRequestListItem) => void,
  onDeactivate: (entity: InvitationRequestListItem) => void,
): GridColDef<InvitationRequestListItem> {
  return {
    field: 'actions',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.actions.heading`,
    }),
    width: 200,
    sortable: true,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      return (
        <Stack direction={'column'} justifyContent={'center'} alignContent={'center'} height={'100%'}>
          <Stack direction={'row'} spacing={1}>
            <Button variant="outlined" color="warning" onClick={() => onDeactivate(params.row)}>
              {intl.formatMessage({
                id: `${localizationSubset}.table.fields.actions.buttons.deactivate.label`,
              })}
            </Button>
            <Button variant="outlined" color="success" onClick={() => onActivate(params.row)}>
              {intl.formatMessage({
                id: `${localizationSubset}.table.fields.actions.buttons.activate.label`,
              })}
            </Button>
          </Stack>
        </Stack>
      );
    },
  };
}
/// ======================= Common columns =======================
function getNameColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'name',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.orgName.heading` }),
    minWidth: 160,
    resizable: true,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const row = params.row as OrganizationListItem;
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', overflowX: 'hidden', textOverflow: 'ellipsis' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          <Stack direction={'row'} spacing={1}>
            <Stack direction={'column'}>
              <Typography variant="subtitle2" noWrap>
                {row.name}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      );
    },
  };
}

function getTenantIdColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'tenantId',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.tenant.heading`,
    }),
    width: 170,
    sortable: true,
    display: 'flex',
    flex: 1,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return `${params.value}`;
    },
  };
}

function getMainContactColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'contact',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.mainContact.heading` }),
    minWidth: 180,
    resizable: true,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const fullName = `${params.row.contact.firstName} ${params.row.contact.lastName}`;
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', overflowX: 'hidden', textOverflow: 'ellipsis' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          <Stack direction={'row'} spacing={1}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
              }}
              src=""
              // src={params.row.contact.avatar}
              fullName={fullName}
              alt={fullName}
            />
            <Stack direction={'column'}>
              <Typography variant="subtitle2" noWrap>
                {fullName}
              </Typography>
              <Typography variant="caption" noWrap color="text.secondary">
                {params.row.contact.email ?? ''}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      );
    },
  };
}

function getPhoneColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'phone',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.phone.heading`,
    }),
    width: 160,
    sortable: true,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row as AgencyListItem;
      return `${row.contact.phoneNumber}`;
    },
  };
}

function getCreatedColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'createdOn',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.createdOn.heading`,
    }),
    width: 110,
    sortable: true,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      return <Chip label={dayjs(params.value).toServerFilterDate()} />;
    },
  };
}
function getAddressColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  return {
    field: 'address',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.address.heading`,
    }),
    width: 160,
    display: 'flex',
    flex: 1,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row as OrganizationListItem;
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', overflowX: 'hidden', textOverflow: 'ellipsis' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          <Stack direction={'row'} spacing={1}>
            <Typography variant="subtitle2" noWrap>
              {row.address.city}
            </Typography>
            <Typography variant="subtitle2" noWrap>
              {row.address.state}
            </Typography>
          </Stack>
          <Typography variant="body1" noWrap>
            {row.address.addressFirst}
          </Typography>
          <Typography variant="body1" noWrap>
            {row.address.addressSecond}
          </Typography>
        </Stack>
      );
    },
  };
}

function getLinesOfBusinessColumn(intl: IntlShape, localizationSubset: string): GridColDef<AgencyListItem> {
  return {
    field: 'linesOfBusiness',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.linesOfBusiness.heading`,
    }),
    width: 130,
    display: 'flex',
    flex: 1,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row as AgencyListItem;
      return (
        <Tooltip title={row.linesOfBusiness.join(', ')}>
          <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {(row.linesOfBusiness ?? []).map((lob, idx) => (
              <Chip key={idx} label={lob} />
            ))}
          </Box>
        </Tooltip>
      );
    },
  };
}

function getAgencyTypeColumn(intl: IntlShape, localizationSubset: string): GridColDef<AgencyListItem> {
  return {
    field: 'agencyType',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.agencyType.heading`,
    }),
    width: 130,
    sortable: true,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      return <Chip label={params.value} />;
    },
  };
}



/// ======================= Facility related columns =======================

function getInviteStatusColumn(intl: IntlShape, localizationSubset: string): GridColDef<OrganizationListItem> {
  const statusColors: Record<OrganizationListItem['status'], ComponentProps<typeof LightChip>['color']> = {
    Active: 'success',
    Pending: 'warning',
    Disconnected: 'error',
    Invited: 'info',
    OnReview: 'default',
  };
  return {
    field: 'status',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.status.heading`,
    }),
    width: 160,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row as OrganizationListItem;
      return (
        <Chip
          color={statusColors[row.status]}
          label={intl.formatMessage({ id: `${localizationSubset}.statuses.${row.status.toLowerCase()}` })}
        />
      );
    },
  };
}
